'use client';

import type { ClassValue } from 'clsx';
import {
  ProductAccessories,
  ProductAccessoriesTab,
  ProductAccessoriesTabLabel,
  ProductAccessoriesTabList,
} from '@vs-shop/commerce-ui/productAccessories';
import { clsxm } from '@vs-shop/utils';
import { Option } from 'effect';
import { useIntl } from 'react-intl';

import { productRecommendedProductsPerPageDefault } from '../../../../../config/pagination';
import { PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL } from '../../../../../config/product';

import ProductRecommendationTab from './ProductRecommendationTab';
import { accessoriesPanelId, accessoriesTabId } from './accessoriesIds';

import { fetchAssociatedProductsPageAtom } from '../../../../../effect/atom/products/fetchAssociatedProductsPage';
import {
  accessoriesTaxonSearchParamAtom,
  changeAccessoriesTaxonAtomFn,
  currentAccessoriesPageAtom,
  recommendationsLastPageLoadedIncludingLoadMoreAtom,
} from '../../../../../effect/atom/products/recommendationsPagination';
import type { TaxonAssociation } from '../../../../../effect/schema/openapi/backend';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

type Props = {
  className?: ClassValue;
  associationTaxons: readonly TaxonAssociation[];
  productCode: string;
  baseUrl: string;
  initialTaxon: string | null;
};

/**
 * rcprofi's accessory block: the shared `@vs-shop/commerce-ui` tab parts fed
 * this tenant's accessories state.
 *
 * Everything the shared parts may not carry is resolved here — which group is
 * current, the atom that changes it, the page the response is on, the fetch
 * behind the panel, and the words. What crosses into the parts is a value, a
 * handler and children.
 */
export default function ProductRecommendationsMenu({
  className,
  associationTaxons,
  productCode,
  baseUrl,
  initialTaxon,
}: Props) {
  const intl = useIntl();

  const currentTaxonOption = useAtomValue(accessoriesTaxonSearchParamAtom);
  const currentPage = useAtomValue(currentAccessoriesPageAtom);

  const changeTaxon = useAtomSet(changeAccessoriesTaxonAtomFn, {
    mode: 'promise',
  });

  const handleTabChange = (value: string) => {
    const taxonCode = value === PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL ? null : value;
    void changeTaxon({ taxonCode, productCode });
  };

  // For controlled tabs value: use atom if set (client navigation), otherwise initial (SSR)
  const activeTabValue = Option.match(currentTaxonOption, {
    onNone: () => initialTaxon ?? PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL,
    onSome: (code) => code,
  });

  const taxonCode = Option.match(currentTaxonOption, {
    onNone: () => initialTaxon,
    onSome: (code) => code,
  });

  const productsResponse = useAtomValue(
    fetchAssociatedProductsPageAtom(
      productCode,
      taxonCode,
      currentPage,
      productRecommendedProductsPerPageDefault
    )
  );

  const lastPageLoadedIncludingLoadMore = useAtomValue(
    recommendationsLastPageLoadedIncludingLoadMoreAtom
  );

  return (
    <ProductAccessories
      value={activeTabValue}
      onValueChange={handleTabChange}
      className={clsxm(className)}
    >
      <ProductAccessoriesTabList>
        {/* ALL */}
        <RecommendationTabTrigger
          name={intl.formatMessage({
            id: 'product.accessories.tab.all',
            defaultMessage: 'Vše',
          })}
          code={PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL}
          isCurrent={activeTabValue === PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL}
        />

        {/* TAXONS */}
        {associationTaxons.map((taxon) => (
          <RecommendationTabTrigger
            key={taxon.code}
            name={taxon.name}
            code={taxon.code}
            isCurrent={activeTabValue === taxon.code}
          />
        ))}
      </ProductAccessoriesTabList>

      {/* ALL */}
      <ProductRecommendationTab
        taxonCode={PRODUCT_RECOMMENDED_PRODUCTS_VALUE_ALL}
        productCode={productCode}
        initialTaxon={initialTaxon}
        baseUrl={baseUrl}
        productsResponse={productsResponse}
        lastPageLoadedIncludingLoadMore={lastPageLoadedIncludingLoadMore}
      />

      {/* TAXONS */}
      {associationTaxons.map((taxon) => (
        <ProductRecommendationTab
          key={taxon.code}
          taxonCode={taxon.code}
          productCode={productCode}
          initialTaxon={initialTaxon}
          baseUrl={baseUrl}
          productsResponse={productsResponse}
          lastPageLoadedIncludingLoadMore={lastPageLoadedIncludingLoadMore}
        />
      ))}
    </ProductAccessories>
  );
}

/**
 * One group's place on the rail.
 *
 * `aria-controls` is composed only for the group that is current, because that
 * is the only one whose panel is in the document: the primitive mounts one, and
 * a reference to a panel nobody rendered is a reference that resolves to
 * nothing. The primitive would supply the same value once it has registered the
 * panel; naming it here is what makes it true at first paint as well.
 */
function RecommendationTabTrigger({
  name,
  code,
  isCurrent,
}: {
  name: string | null;
  code: string;
  isCurrent: boolean;
}) {
  return (
    <ProductAccessoriesTab
      value={code}
      id={accessoriesTabId(code)}
      {...(isCurrent ? { 'aria-controls': accessoriesPanelId(code) } : {})}
    >
      <ProductAccessoriesTabLabel>{name ?? code}</ProductAccessoriesTabLabel>
    </ProductAccessoriesTab>
  );
}
